import React, { useState, useEffect, useMemo, useCallback} from 'react';
import { useFetchCurrentUserPermissionsQuery } from '../../store/slices/currentUserPermissionApiSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { matchPath } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ReactComponent as HouseIcon } from '../../assets/icons/house.svg';
import { ReactComponent as HouseIconW } from '../../assets/icons/whouse.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/userm.svg';
import { ReactComponent as UserIconW } from '../../assets/icons/userw.svg';
import { ReactComponent as InvestigationListIcn } from '../../assets/icons/Investigation_teal_icon.svg';
import { ReactComponent as InvestigationListIcnW } from '../../assets/icons/Investigation_white_icon.svg';
import LoadingSpinner from '../../components/LoadingSpinner';
import { ReactComponent as MotorListIcon } from '../../assets/icons/motorlisticon.svg';
import { ReactComponent as MotorListIconW } from '../../assets/icons/motorlisticonw.svg';
import { ReactComponent as EngDashboard } from '../../assets/icons/engboard.svg';
import { ReactComponent as IengDashboard } from '../../assets/icons/iengboard.svg';
import { ReactComponent as ApprovalIcon } from '../../assets/icons/approval.svg';
import { ReactComponent as WhiteApprovalIcon } from '../../assets/icons/whiteApproval.svg';
import { useTranslation } from 'hooks/useTranslation';
import { ROUTE_CONSTANTS } from 'constants/routeConstant';

interface SidebarProps {
  open: boolean;
  handleDrawerToggle: () => void;
  drawerWidth: number;
  setDrawerWidth: (width: number) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  handleDrawerToggle,
  drawerWidth,
  setDrawerWidth,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: menuData, isLoading } = useFetchCurrentUserPermissionsQuery();
  const [isExpanded, setIsExpanded] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = useMemo(() => {
    if (!menuData) return [];

    return menuData.roleNavigations
      .slice() // create a shallow copy to avoid mutating original data
      .sort((a, b) => a.sequence - b.sequence) // sort by sequence
      .map((nav) => ({
        text: nav.description,
        menuName: nav.menuName,
        path: nav.url,
        icon: nav.icon,
        permissions: {
          view: menuData.rolePermissions[nav.menuName] ?? true,
        },
      }))
      .filter((item) => item.permissions.view);
  }, [menuData]);

  // Drawer width logic
  useEffect(() => {
    if (isMobile) {
      setDrawerWidth(236);
    } else {
      const timeoutId = setTimeout(() => {
        setDrawerWidth(isExpanded ? 236 : 96);
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isExpanded, isMobile, setDrawerWidth]);

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };
  // Icon selection logic
  const getIcon = (menuName: string, isSelected: boolean) => {
    switch (menuName) {
      case 'Home':
        return isSelected ? (
          <HouseIcon />
        ) : (
          <HouseIconW style={{ height: '28px', width: '28px' }} />
        );
      case 'TicketList':
        return <ConfirmationNumberIcon />;
      case 'UserManagement':
        return isSelected ? <UserIcon /> : <UserIconW style={{ height: '28px', width: '28px' }} />;
      case 'InvestigationList':
        return isSelected ? (
          <InvestigationListIcn />
        ) : (
          <InvestigationListIcnW style={{ height: '28px', width: '28px' }} />
        );
      case 'MotorList':
        return isSelected ? (
          <MotorListIcon />
        ) : (
          <MotorListIconW style={{ height: '28px', width: '28px' }} />
        );
        case 'EngineeringDashboard':
        return isSelected ? (
          <IengDashboard />
        ) : (
          <EngDashboard style={{ height: '28px', width: '28px' }} />
        );
        case 'ApprovalTasks':
        return isSelected ? (
          <ApprovalIcon />
        ) : (
          <WhiteApprovalIcon style={{ height: '28px', width: '28px' }} />
        );
      case 'ReleaseNotes':
        return <NewReleasesOutlinedIcon />;
      case 'DriveInvestigationList':
        return isSelected ? (
          <InvestigationListIcn />
        ) : (
          <InvestigationListIcnW style={{ height: '28px', width: '28px' }} />
        );  
      case 'ElektronikonInvestigationList':
        return isSelected ? (
          <InvestigationListIcn />
        ) : (
          <InvestigationListIcnW style={{ height: '28px', width: '28px' }} />
        );  
      default:
        return null;
    }
  };

  // Route selection logic
  const isRouteSelected = useCallback(
    (routePath: string) => {
      // For engineering-dashboard, match both exact and child routes
      if (routePath === `/${ROUTE_CONSTANTS.ENGINEERING_DASHBOARD}`) {
        return location.pathname === `/${ROUTE_CONSTANTS.ENGINEERING_DASHBOARD}` || 
               location.pathname.startsWith(`/${ROUTE_CONSTANTS.ENGINEERING_DASHBOARD}/`);
      }
      // For approval-tasks, match both exact and child routes
        if (routePath === `/${ROUTE_CONSTANTS.APPROVAL_TASKS}`) {
          return location.pathname === `/${ROUTE_CONSTANTS.APPROVAL_TASKS}` || 
                location.pathname.startsWith(`/${ROUTE_CONSTANTS.APPROVAL_TASKS}/`);
        }
        // For investigation-list, match both exact and child routes
        if (routePath === `/${ROUTE_CONSTANTS.INVESTIGATION_LIST}`) {
          return location.pathname === `/${ROUTE_CONSTANTS.INVESTIGATION_LIST}` ||
                location.pathname.startsWith(`/${ROUTE_CONSTANTS.INVESTIGATION_LIST}/`);
        }
        // For drive-investigation-list, match both exact and child routes
        if (routePath === `/${ROUTE_CONSTANTS.DRIVE_INVESTIGATION_LIST}`) {
          return location.pathname === `/${ROUTE_CONSTANTS.DRIVE_INVESTIGATION_LIST}` ||
                location.pathname.startsWith(`/${ROUTE_CONSTANTS.DRIVE_INVESTIGATION_LIST}/`);
        }
        if (routePath === `/${ROUTE_CONSTANTS.ELEKTRONIKON_INVESTIGATION_LIST}`) {
          return location.pathname === `/${ROUTE_CONSTANTS.ELEKTRONIKON_INVESTIGATION_LIST}` ||
                location.pathname.startsWith(`/${ROUTE_CONSTANTS.ELEKTRONIKON_INVESTIGATION_LIST}/`);
        }
        if (routePath === `/${ROUTE_CONSTANTS.TICKETS}`) {
          return location.pathname === `/${ROUTE_CONSTANTS.TICKETS}` || 
                location.pathname === `/${ROUTE_CONSTANTS.TICKET_CHECK}` ||
                location.pathname.startsWith(`/${ROUTE_CONSTANTS.TICKETS}/`);
        }
        // For motor-list, match both Motor List and Edit pages
        if (routePath === `/${ROUTE_CONSTANTS.MOTOR_LIST}`) {
          return location.pathname === `/${ROUTE_CONSTANTS.MOTOR_LIST}` || 
                location.pathname === `/${ROUTE_CONSTANTS.MOTOR_EDIT}` ||
                location.pathname.startsWith(`/${ROUTE_CONSTANTS.MOTOR_LIST}/`);
        }
      return !!matchPath({ path: routePath, end: true }, location.pathname);
    },
    [location.pathname]
  );

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {!isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            padding: 2,
            mt: '72px',
            minHeight: 64,
          }}
        >
          {isExpanded && (
            <Typography
              variant="subtitle1"
              color="white"
              sx={{
                ml: 1,
                fontWeight: 600,
                textAlign: 'center',
                width: '100%',
                opacity: isExpanded ? 1 : 0,
                transition: theme.transitions.create(['opacity'], {
                  duration: theme.transitions.duration.standard,
                  delay: isExpanded ? 150 : 0,
                }),
              }}
            >
              {t('sidebar.menus')}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1,
          position: 'relative',  
          scrollbarWidth: 'none',
        }}
      >
        <List sx={{ mt: 0, pb: 2 }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            menuItems.map((item, index) => {
              const selected = isRouteSelected(item.path);
              const icon = getIcon(item.menuName, selected);

            return (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemButton
                  onClick={() => {
                    if (item.menuName === 'Home') {
                      navigate(item.path, { state: { resetToSearch: true, refreshData: true } });
                    } else {
                      navigate(item.path, { state: { refreshData: true } });
                    }
                    if (isMobile) handleDrawerToggle();
                  }}
                  sx={{
                    borderRadius: 2,
                    mb: 1.5,
                    mx: 1,
                    backgroundColor: selected ? 'rgba(223, 228, 231, 1)' : 'transparent',
                    color: selected ? '#054e5a' : '#fff',
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                    alignItems: 'center',
                    minHeight: 48,
                    px: isExpanded ? 2 : 1,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: theme.transitions.create(
                      ['background-color', 'color', 'padding', 'transform'],
                      {
                        duration: theme.transitions.duration.short,
                      }
                    ),
                    '&:hover': {
                      backgroundColor: selected
                        ? 'rgba(223, 228, 231, 1)'
                        : 'rgba(223, 228, 231, 0.21)',
                    },
                    '&:active': {
                      transform: 'translateX(2px) scale(0.98)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 24,
                      color: selected ? '#054e5a' : '#fff',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transition: theme.transitions.create(['color', 'transform'], {
                        duration: theme.transitions.duration.short,
                      }),
                      transform: selected ? 'scale(1.1)' : 'scale(1)',
                      '& svg': {
                        transition: theme.transitions.create(['transform'], {
                          duration: theme.transitions.duration.short,
                        }),
                      },
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  {isExpanded && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        ml: 1,
                        opacity: isExpanded ? 1 : 0,
                        transform: isExpanded ? 'translateX(0)' : 'translateX(-10px)',
                        transition: theme.transitions.create(['opacity', 'transform'], {
                          duration: theme.transitions.duration.standard,
                          delay: isExpanded ? 100 : 0,
                        }),
                        '& .MuiListItemText-primary': {
                          fontWeight: selected ? 700 : 400,
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Box>
    </Box>
  );

  return (
    <Box>
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={open}
        onClose={handleDrawerToggle}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            minWidth: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#054e5a',
            color: '#ffffff',
            transition: theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            padding: '0px !important',
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawerContent}
      </Drawer>

      {!isLoading && !isMobile && !isSmall && open && (
        <IconButton
          onClick={handleToggleExpanded}
          aria-label="toggle sidebar"
          color="inherit"
          edge="end"
          size="small"
          disableRipple
          disableFocusRipple
          sx={{
            position: 'fixed',
            top: 90,
            left: drawerWidth - 12,
            width: 24,
            height: 24,
            bgcolor: '#fff',
            color: '#000',
            borderRadius: '50%',
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
            zIndex: 1300,
            transition: theme.transitions.create(['left', 'transform', 'box-shadow'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            '&:hover': {
              bgcolor: '#f0f0f0',
              transform: 'scale(1.1)',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '& svg': {
              transition: theme.transitions.create(['transform'], {
                duration: theme.transitions.duration.short,
              }),
            },
          }}
        >
          {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      )}
    </Box>
  );
};

export default Sidebar;
